import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Minus, Plus, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Lightweight product-lead capture modal (Catalogue Phase 4).
 * Used by product card "QUOTE" button and ProductDetail "Get Quote / Bulk Enquiry".
 * Prefills name/email/phone from user profile if logged in.
 */
export default function LeadCaptureModal({ open, onClose, product, user, defaultQuantity = 1, source = "catalogue_quick_quote" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", quantity: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      company: user?.company || "",
      message: "",
      quantity: Math.max(1, defaultQuantity || product?.min_order_qty || 1),
    });
    setDone(null);
  }, [open, product, user, defaultQuantity]);

  if (!open || !product) return null;

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const stepQty = (delta) => setForm(f => ({ ...f, quantity: Math.max(1, (f.quantity || 1) + delta) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/product-leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        message: form.message.trim(),
        product_id: product.product_id,
        quantity: Math.max(1, parseInt(form.quantity) || 1),
        source,
      });
      setDone(data);
      toast.success(data.message || "Request submitted!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const price = product.offer_price && product.offer_price > 0 && product.offer_price < (product.retail_price || product.price || 0)
    ? product.offer_price
    : (product.retail_price || product.price || 0);
  const estTotal = price * (parseInt(form.quantity) || 1);

  return (
    <div data-testid="lead-capture-modal" className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-3 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl rounded-sm">
        <div className="sticky top-0 bg-[#002FA7] text-white px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider opacity-80">{source === "product_detail_bulk" ? "Bulk Enquiry" : "Quick Quote"}</div>
            <h3 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>{product.name}</h3>
          </div>
          <button data-testid="lead-capture-close" onClick={onClose} className="text-white/90 hover:text-white"><X size={20} /></button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <CheckCircle size={56} weight="fill" className="mx-auto text-emerald-500 mb-3" />
            <h4 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Request Received!</h4>
            <p className="text-sm text-slate-600 mb-4">{done.message}</p>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-700 mb-4">
              <div>Reference: <b>{done.lead_id}</b></div>
              {done.assigned_to && <div className="mt-1">Assigned to: <b>{done.assigned_to}</b></div>}
            </div>
            <button data-testid="lead-capture-close-success" onClick={onClose} className="bg-[#002FA7] hover:bg-[#00227A] text-white px-6 py-2 text-sm font-bold">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
              {product.image_urls?.[0] && <img src={product.image_urls[0]} alt="" className="w-14 h-14 object-cover rounded-sm" />}
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">{product.brand_name || ""}</div>
                <div className="text-xs text-slate-600 line-clamp-2">{product.model_number && `Model: ${product.model_number}`}</div>
                {price > 0 && <div className="text-sm font-bold text-[#002FA7] mt-0.5">₹{price.toLocaleString("en-IN")} / unit</div>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Full Name *" required testId="lead-name" value={form.name} onChange={v => setField("name", v)} />
              <Field label="Phone *" required testId="lead-phone" value={form.phone} onChange={v => setField("phone", v)} placeholder="+91 9xxxxxxxxx" />
              <Field label="Email" testId="lead-email" value={form.email} onChange={v => setField("email", v)} type="email" />
              <Field label="Company (optional)" testId="lead-company" value={form.company} onChange={v => setField("company", v)} />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">Quantity</label>
              <div className="flex items-center gap-2">
                <button type="button" data-testid="lead-qty-minus" onClick={() => stepQty(-1)} className="w-9 h-9 border border-slate-300 hover:bg-slate-50 flex items-center justify-center"><Minus size={14} /></button>
                <input data-testid="lead-qty-input" type="number" min="1" value={form.quantity} onChange={e => setField("quantity", Math.max(1, parseInt(e.target.value) || 1))} className="w-20 h-9 border border-slate-300 text-center font-bold" />
                <button type="button" data-testid="lead-qty-plus" onClick={() => stepQty(1)} className="w-9 h-9 border border-slate-300 hover:bg-slate-50 flex items-center justify-center"><Plus size={14} /></button>
                {price > 0 && <span className="text-xs text-slate-500 ml-3">Est. total: <b className="text-slate-900">₹{estTotal.toLocaleString("en-IN")}</b></span>}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">Message / Requirement</label>
              <textarea data-testid="lead-message" value={form.message} onChange={e => setField("message", e.target.value)}
                rows={3} placeholder="Share installation site, timeline, or any specific requirement…"
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none" />
            </div>

            <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-2">
              Our team responds within 24 hours on business days. No spam — we use your details only for this enquiry.
            </div>

            <div className="flex gap-2 pt-1">
              <button type="button" data-testid="lead-cancel-btn" onClick={onClose} className="flex-1 border border-slate-300 hover:bg-slate-50 text-slate-700 py-2.5 text-sm font-bold">Cancel</button>
              <button type="submit" data-testid="lead-submit-btn" disabled={submitting} className="flex-1 bg-[#002FA7] hover:bg-[#00227A] text-white py-2.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                <PaperPlaneTilt size={14} weight="fill" /> {submitting ? "Submitting…" : "Send Request"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, testId, type = "text", placeholder = "" }) {
  return (
    <div>
      <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">{label}</label>
      <input type={type} required={required} data-testid={testId} placeholder={placeholder}
        value={value} onChange={e => onChange(e.target.value)}
        className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none" />
    </div>
  );
}
